import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./MapMonitoring.module.css";

import ContextMenu from "./components/ContextMenu/ContextMenu";
import AddIncidentModal from "./components/AddMarkerModal/AddIncidentModal";
import { MapSearchPanel } from "./components/MapSearchPanel/MapSearchPanel";
import AddTboModal from "./components/AddTboModal/AddTboModal";
import MapLayersBar from "./components/MapLayersBar/MapLayersBar";
import DrawingTools from "./components/DrawingTools/DrawingTools";
import AddGarageModal from "./components/AddGarageModal/AddGarageModal";
import AddContainerPlatformsModal from "./components/AddContainerPlatformsModal/AddContainerPlatformsModal";
import { ShapeMetaModal } from "./components/ModalGeofence/ModalGeofence";

import { MapFiltersPanel } from "./components/MapFiltersPanel/MapFiltersPanel";

import { getSelection } from "./services/mapSelectionStorage";
import { mapDataBySelection } from "./services/mapDataBySelection";
import {
  extractPlannedRoutesFromApiPayload,
  plannedDtosToMapRoutes,
} from "../../utils/plannedRoutesToMapRoutes";
import { routePlanningService } from "../../services/planRoute/route-planning-service";
import type { MapRouteLocationState } from "../../types/navigation/mapRouteState";
import type { Route } from "./types";
import { geofenceToDrawnShape } from "../../utils/geofenceToDrawnShape";

import { useYandexMap } from "./hooks/useYandexMap";
import { useCustomMarks } from "./hooks/useCustomMarks";
import { useMapFilters } from "./hooks/useMapFilters";
import { useRoutes } from "./hooks/useRoutes";
import { useMapLayers } from "./hooks/useMapLayers";
import type {
  ContextMenuPosition,
  ContainerPoint,
  DrawnShapeType,
  RestrictionZone,
} from "./types";

import { useFetchPanelData } from "../../hooks/map/useFetchPanelData";
import { toCoordinatesQuery } from "../../utils/toCoordinatesQuery";
import EditContainerPlatformsModal from "./components/EditContainerPlatformsModal/EditContainerPlatformsModal";
import { subscribeToIds } from "../../signalR/signalR";
import {
  onPositionUpdate,
  onPositionsSnapshot,
  TransportPositionDto,
} from "../../signalR/signalR";
import EditGeofenceModal, {
  EditGeofenceModalHandle,
} from "./components/EditGeofenceModal/EditGeofenceModal";
import {
  buildMapVehicleSubscriptionIds,
  resolveSelectedVehicleEntityIdsFromPanel,
  resolveVehicleNumberForTransportPosition,
} from "./utils/resolveSelectedVehicleEntityIdsFromPanel";

interface MapMonitoringProps {
  containerPoints?: ContainerPoint[];
  routeIdFromKP?: string;
  routeNameFromKP?: string;
  hideOtherRoutes?: boolean;
}

// type MapMode = "create-geozone" | "create-container-site" | null;

/** Логи цепочки planned-routes + фильтра карты (отключите вручную на `false` при необходимости). */
const DEBUG_PLANNED_ROUTES = process.env.NODE_ENV !== "production";

export default function MapMonitoring({
  containerPoints = [],
  routeIdFromKP,
  routeNameFromKP,
  hideOtherRoutes = true,
}: MapMonitoringProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();

  const mapLocationState = (location.state as MapRouteLocationState) ?? null;
  const mapMode = mapLocationState?.mapMode ?? null;
  const planningPreviewFromNav = mapLocationState?.planningPreview;
  const handledModeRef = useRef(false);

  const [isOpenContainerPlatformsModal, setOpenContainerPlatforms] = useState<{
    isOpen: boolean;
    id: string;
  }>({ isOpen: false, id: "" });
  const [isOpenGeofenceModal, setOpenGeofenceModal] = useState<{
    isOpen: boolean;
    id: string;
  }>({ isOpen: false, id: "" });
  const geofenceModalRef = useRef<EditGeofenceModalHandle | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(
    () => new Date().toISOString().split("T")[0],
  );
  const { customMarks } = useCustomMarks();
  const { criticalActive, currentColors } = useMapFilters();
  const { data: panelData, refetch: refetchData } = useFetchPanelData();
  const panelDataRef = useRef(panelData);
  panelDataRef.current = panelData;
  const lastVehicleSubSigRef = useRef<string | null>(null);

  const [mapVehicles, setMapVehicles] = useState<
    Array<{
      id: string;
      name: string;
      coords: [number, number];
      hintContent: string;
    }>
  >([]);
  const [mapZones, setMapZones] = useState<RestrictionZone[]>([]);
  const [mapContainerPlatforms, setMapContainerPlatforms] = useState<
    Array<{ id: string; name: string; centerPoint: string }>
  >([]);
  const [isGeofenceModalCollapsed, setGeofenceModalCollapsed] = useState(false);
  const vehiclesRef = useRef(
    new Map<
      number,
      {
        id: string;
        name: string;
        hintContent: string;
        coords: [number, number];
        recordedAtUtc: string;
      }
    >(),
  );
  const rafRef = useRef<number | null>(null);

  function flushVehiclesToState() {
    // батчим обновления, чтобы не ререндерить 20 раз в секунду
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setMapVehicles(
        Array.from(vehiclesRef.current.values()).map((v) => ({
          id: v.id,
          name: v.name,
          hintContent: v.hintContent,
          coords: v.coords,
        })),
      );
    });
  }

  const flushVehiclesToStateRef = useRef(flushVehiclesToState);
  flushVehiclesToStateRef.current = flushVehiclesToState;

  useEffect(() => {
    if (!panelData?.vehicleGroups?.length || vehiclesRef.current.size === 0)
      return;
    const sep = " · ";
    let changed = false;
    for (const [transportId, row] of vehiclesRef.current.entries()) {
      const resolved = resolveVehicleNumberForTransportPosition(
        panelData,
        String(transportId),
      );
      if (!resolved) continue;
      const i = row.name.lastIndexOf(sep);
      const speedPart = i >= 0 ? row.name.slice(i + sep.length) : "0 км/ч";
      const nextHint = `${resolved}${sep}${speedPart}`;
      if (row.hintContent === nextHint && row.name === nextHint) continue;
      vehiclesRef.current.set(transportId, {
        ...row,
        hintContent: nextHint,
        name: nextHint,
      });
      changed = true;
    }
    if (changed) flushVehiclesToStateRef.current();
  }, [panelData]);

  useEffect(() => {
    function upsertFromDto(p: TransportPositionDto) {
      // ВАЖНО: Яндекс обычно ждёт [lat, lon] (у тебя так и есть)
      const coords: [number, number] = [p.lat, p.lon];

      const prev = vehiclesRef.current.get(p.transportId);

      // защита от "старых" пакетов: не обновляем, если recordedAtUtc старее
      if (
        prev &&
        new Date(p.recordedAtUtc).getTime() <=
          new Date(prev.recordedAtUtc).getTime()
      ) {
        return;
      }

      const ext = p.extId?.trim();
      const resolved =
        (ext
          ? resolveVehicleNumberForTransportPosition(panelDataRef.current, ext)
          : null) ??
        resolveVehicleNumberForTransportPosition(
          panelDataRef.current,
          String(p.transportId),
        );
      const sep = " · ";
      const prevNum = prev?.hintContent?.split(sep)?.[0]?.trim();
      const num = resolved ?? prevNum ?? String(p.transportId);
      const hint = `${num}${sep}${p.speed} км/ч`;

      vehiclesRef.current.set(p.transportId, {
        id: String(p.transportId),
        name: hint,
        hintContent: hint,
        coords,
        recordedAtUtc: p.recordedAtUtc,
      });

      flushVehiclesToState();
    }
    const offSnap = onPositionsSnapshot((items) => {
      for (const p of items) upsertFromDto(p);
    });

    const offUpd = onPositionUpdate((p) => {
      upsertFromDto(p);
    });

    return () => {
      offSnap();
      offUpd();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const recomputeRequestIdRef = useRef(0);
  const recomputeMapData = useCallback(async () => {
    const requestId = ++recomputeRequestIdRef.current;

    const selection = getSelection();
    const query = toCoordinatesQuery(selection);

    // если вообще ничего не выбрано — чистим карту и выходим
    const hasAny =
      (query.geofenceGroups?.length ?? 0) > 0 ||
      (query.geofences?.length ?? 0) > 0 ||
      (selection.vehicle?.length ?? 0) > 0;

    if (!hasAny) {
      setMapZones([]);
      setMapContainerPlatforms([]);
      // ✅ Чистим машины тоже
      lastVehicleSubSigRef.current = null;
      vehiclesRef.current.clear();
      setMapVehicles([]);
      await subscribeToIds([]);
      return;
    }
    // ---------- Машины ----------
    const ids = buildMapVehicleSubscriptionIds(panelDataRef.current, selection);
    const subSig = ids.slice().sort().join("\u0000");
    if (subSig !== lastVehicleSubSigRef.current) {
      lastVehicleSubSigRef.current = subSig;
      vehiclesRef.current.clear();
      setMapVehicles([]);
    }

    // const subPromise = subscribeToIds(ids).catch(() => console.log("Ошибка при обработке подписки"));

    let data;
    try {
      data = await mapDataBySelection(query);
    } catch {
      // при ошибке лучше не менять текущее состояние, либо очистить — на твой вкус
      return;
    }

    if (requestId !== recomputeRequestIdRef.current) return;

    const drawnZones = geofenceToDrawnShape(data.geofences ?? []);
    const nextZones: RestrictionZone[] = drawnZones.map((z) => ({
      id: String(z.id),
      name: String(z.name ?? ""),
      type: "polygon",
      coordinates: z.coordinates,
      style: z.style,
    }));

    const nextContainerPlatforms = (data.containerPlatforms ?? []).map(
      (cp) => ({
        id: String(cp.id),
        name: String(cp.name ?? ""),
        centerPoint: String(cp.centerPoint ?? ""),
      }),
    );

    setMapZones(nextZones);
    setMapContainerPlatforms(nextContainerPlatforms);
  }, []);

  // 3. Дебаунс для recomputeMapData
  const debouncedRecompute = useRef<any>(0);

  useEffect(() => {
    clearTimeout(debouncedRecompute.current);
    debouncedRecompute.current = setTimeout(() => {
      recomputeMapData();
    }, 100);

    const handleSelectionChange = () => {
      clearTimeout(debouncedRecompute.current);
      debouncedRecompute.current = setTimeout(() => {
        recomputeMapData();
      }, 100);
    };

    window.addEventListener("map-selection-changed", handleSelectionChange);

    return () => {
      clearTimeout(debouncedRecompute.current);
      window.removeEventListener(
        "map-selection-changed",
        handleSelectionChange,
      );
    };
  }, [recomputeMapData]);

  const {
    routes,
    activeRouteIds,
    setActiveRoutes,
    getVisibleRoutes,
    createRouteFromCoordinates,
    updateRoute,
    setPlannedRoutes,
    clearPlannedRoutes,
  } = useRoutes();

  /** Синхронизация с выбором ТС в панели (machineIds в storage — это `number`, в API нужны `id` из panelData). */
  const [, setMapSelectionVersion] = useState(0);
  useEffect(() => {
    const onChange = () => setMapSelectionVersion((v) => v + 1);
    window.addEventListener("map-selection-changed", onChange);
    return () => window.removeEventListener("map-selection-changed", onChange);
  }, []);

  /**
   * Entity id ТС из `panelData.vehicleGroups` → в GET planned-routes как `transportIds`.
   * Превью планирования — без фильтра.
   */
  const transportIdsForPlannedApi = useMemo((): string[] | undefined => {
    if (planningPreviewFromNav) return undefined;
    return resolveSelectedVehicleEntityIdsFromPanel(panelData, getSelection());
  }, [planningPreviewFromNav, panelData]);

  const transportIdsForPlannedApiKey =
    transportIdsForPlannedApi?.slice().sort().join(",") ?? "";

  const plannedFetchSeqRef = useRef(0);
  useEffect(() => {
    const routeDate =
      planningPreviewFromNav?.routeDate ??
      new Date().toISOString().split("T")[0];
    const algorithm = planningPreviewFromNav?.algorithm?.trim()
      ? planningPreviewFromNav.algorithm.trim()
      : undefined;

    const seq = ++plannedFetchSeqRef.current;

    if (DEBUG_PLANNED_ROUTES) {
      console.log("[MapMonitoring/planned] GET params", {
        seq,
        routeDate,
        algorithm,
        planningPreviewFromNav,
        transportIds: transportIdsForPlannedApi,
      });
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await routePlanningService.getPlannedRoutes(
          routeDate,
          algorithm,
          transportIdsForPlannedApi?.length
            ? { transportIds: transportIdsForPlannedApi }
            : undefined,
        );
        const list = extractPlannedRoutesFromApiPayload(res.data);
        if (DEBUG_PLANNED_ROUTES) {
          console.log("[MapMonitoring/planned] GET planned-routes ответ", {
            seq,
            cancelled,
            httpStatus: res.status,
            dtoCount: list.length,
          });
        }
        if (cancelled) return;
        const mapped = plannedDtosToMapRoutes(list, algorithm);
        setPlannedRoutes(mapped);
      } catch (err) {
        if (DEBUG_PLANNED_ROUTES) {
          console.error("[MapMonitoring/planned] GET planned-routes error", {
            seq,
            err,
          });
        }
        if (!cancelled) setPlannedRoutes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    planningPreviewFromNav?.routeDate,
    planningPreviewFromNav.algorithm,
    location.key,
    setPlannedRoutes,
    transportIdsForPlannedApiKey,
    planningPreviewFromNav,
    transportIdsForPlannedApi,
  ]);

  /**
   * Фильтрация по ТС делается на бэке (query transportIds). Повторно резать по `transportId` здесь нельзя:
   * в DTO часто другой id, чем `VehicleForPanel.id` из панели — линии пропадали при 200 и dtoCount > 0.
   */
  const routesForMap = useMemo(
    (): Route[] => getVisibleRoutes(),
    [getVisibleRoutes],
  );

  // Если мы пришли на карту в режиме "Изменить вручную" (preview из планирования),
  // то эти planned-маршруты считаем временными и чистим их при уходе со страницы карты,
  // чтобы на следующем заходе не подтягивались из LocalStorage.
  useEffect(() => {
    if (!planningPreviewFromNav) return;
    return () => {
      clearPlannedRoutes();
    };
  }, [planningPreviewFromNav, clearPlannedRoutes]);

  const { layersConfig, toggleLayerVisibility, getLayerVisibility } =
    useMapLayers();

  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(
    null,
  );
  const [modalIncident, setModalIncident] = useState<[number, number] | null>(
    null,
  );
  const [modalContainerPlatforms, setModalContainerPlatforms] = useState<
    [number, number] | null
  >(null);
  const [garageCoords, setGarageCoords] = useState<[number, number] | null>(
    null,
  );
  const [isOpenGarage, setOpenGarage] = useState(false);
  const [tboCoords, setTboCoords] = useState<[number, number] | null>(null);
  const [isOpenTbo, setOpenTbo] = useState(false);

  const [shapeModal, setShapeModal] = useState<null | {
    type: DrawnShapeType;
    coordinates: Array<[number, number]>;
    resolve: (v: { name: string; description: string } | null) => void;
  }>(null);

  const handleContextMenu = useCallback(
    (x: number, y: number, coords: [number, number]) => {
      setContextMenu({ x, y, coords });
    },
    [],
  );

  const handleBottomRightButtonClick = async () => {
    try {
      const response = await routePlanningService.excelExport(selectedDate);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      // можно вытащить имя файла из headers
      const contentDisposition = response.headers["content-disposition"];
      let fileName = "planned-routes.xlsx";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match?.[1]) fileName = match[1];
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.log("Ошибка выгрузки");
    }
  };

  const kpCoords = useMemo(() => {
    if (containerPoints?.length >= 2)
      return containerPoints.map((p) => p.coords);
    return [];
  }, [containerPoints]);

  const kpRouteIdRef = useRef(routeIdFromKP || "kp-route");
  const lastSigRef = useRef("");
  const isAnyModalOpen = Boolean(
    modalIncident || modalContainerPlatforms || shapeModal,
  );

  useEffect(() => {
    if (!kpCoords || kpCoords.length < 2) return;

    const sig = JSON.stringify(kpCoords);
    if (sig === lastSigRef.current) return;
    lastSigRef.current = sig;

    const kpRouteId = routeIdFromKP || kpRouteIdRef.current;
    const kpRouteName = routeNameFromKP || `Маршрут по КП (${kpCoords.length})`;

    createRouteFromCoordinates(kpRouteId, kpRouteName, kpCoords);
    setActiveRoutes([kpRouteId]);

    if (hideOtherRoutes) {
      routes.forEach((r) => {
        // Не скрываем маршруты с бэка (planned): иначе после обновления `routes` эффект снова
        // срабатывает и ставит им visible:false — линии пропадают после окончания запроса.
        if (r.plannedMeta) return;
        if (r.id !== kpRouteId && r.visible !== false) {
          if (DEBUG_PLANNED_ROUTES) {
            console.log(
              "[MapMonitoring/planned] KP effect hideOtherRoutes → visible:false",
              r.id,
            );
          }
          updateRoute(r.id, { visible: false });
        }
      });
    }
  }, [
    kpCoords,
    routeIdFromKP,
    routeNameFromKP,
    createRouteFromCoordinates,
    setActiveRoutes,
    hideOtherRoutes,
    routes,
    updateRoute,
  ]);

  const {
    mapRef,
    searchByAddress,
    drawMode,
    startDrawingPolygon,
    finishDrawingOrEditing,
    cancelDrawingOrEditing,
    getGeofencePointsFromMap,
    startEditGeofenceContour,
  } = useYandexMap({
    customMarks,
    currentColors,
    criticalActive,
    routes: routesForMap,
    activeRouteIds,
    visibleZones: mapZones,
    visibleVehicles: mapVehicles.map((v) => ({
      id: v.id,
      coords: v.coords,
      name: v.name,
      hintContent: v.hintContent,
    })),
    containerPlatforms: mapContainerPlatforms,
    onGeofenceCreated: refetchData,
    layersConfig,
    onContextMenu: handleContextMenu,
    searchCity: "Улан-Удэ",
    onRequestShapeMeta: ({ type, coordinates }) =>
      new Promise((resolve) => setShapeModal({ type, coordinates, resolve })),
    setOpenContainerPlatforms: setOpenContainerPlatforms,
    setOpenGeofenceModal: setOpenGeofenceModal,
    onFinishEditContour: () => geofenceModalRef.current?.save(),
  });

  useEffect(() => {
    if (!mapMode) return;
    if (handledModeRef.current) return;

    handledModeRef.current = true;

    if (mapMode === "create-geozone") {
      alert(
        "Чтобы добавить геозону: включите режим рисования геозон (кнопка «Нарисовать геозону») и нарисуйте область на карте.",
      );
    }

    if (mapMode === "create-container-site") {
      alert(
        "Чтобы добавить контейнерную площадку: нажмите ПРАВОЙ кнопкой мыши по карте и выберите «Добавить контейнерную площадку».",
      );
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [mapMode, navigate, location.pathname]);

  return (
    <div
      className={`${styles.wrapper} ${isAnyModalOpen ? styles.modalOpen : ""}`}
    >
      <div ref={mapRef} className={styles.map} />

      <MapFiltersPanel
        geofences={panelData?.geofenceGroups}
        machines={panelData?.vehicleGroups}
        containerPlatforms={panelData?.containerPlatformsGroups}
      />

      <div className={styles.topRight}>
        <MapSearchPanel city="Улан-Удэ" onSearch={searchByAddress} />
        <MapLayersBar
          getLayerVisibility={getLayerVisibility}
          toggleLayerVisibility={toggleLayerVisibility}
        />
        <DrawingTools
          drawMode={drawMode}
          onStartPolygon={startDrawingPolygon}
          onFinish={finishDrawingOrEditing}
          onCancel={cancelDrawingOrEditing}
        />
      </div>

      <div className={styles.bottomRightGroup}>
        <input
          type="date"
          className={styles.bottomRightDate}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <button
          type="button"
          className={styles.bottomRightAction}
          onClick={handleBottomRightButtonClick}
        >
          Выгрузить маршруты
        </button>
      </div>

      {contextMenu && (
        <ContextMenu
          position={contextMenu}
          onClose={() => setContextMenu(null)}
          onAddIncident={() => {
            setModalIncident(contextMenu.coords);
            setContextMenu(null);
          }}
          onAddContainerPlatforms={() => {
            setModalContainerPlatforms(contextMenu.coords);
            setContextMenu(null);
          }}
          onAddGarage={() => {
            setGarageCoords(contextMenu.coords);
            setOpenGarage(true);
          }}
          onAddTbo={() => {
            setTboCoords(contextMenu.coords);
            setOpenTbo(true);
          }}
        />
      )}

      {modalIncident && (
        <AddIncidentModal
          coords={modalIncident}
          onClose={() => setModalIncident(null)}
        />
      )}

      {modalContainerPlatforms && (
        <AddContainerPlatformsModal
          coords={modalContainerPlatforms}
          onClose={() => setModalContainerPlatforms(null)}
          refetchData={refetchData}
        />
      )}
      {isOpenGarage && garageCoords && (
        <AddGarageModal
          coords={garageCoords}
          onClose={() => {
            setOpenGarage(false);
          }}
          refetchData={refetchData}
        />
      )}
      {isOpenTbo && tboCoords && (
        <AddTboModal
          coords={tboCoords}
          onClose={() => {
            setOpenTbo(false);
            setTboCoords(null);
          }}
          refetch={refetchData}
        />
      )}

      {shapeModal && (
        <ShapeMetaModal
          open={true}
          coordinates={shapeModal.coordinates}
          onClose={() => {
            shapeModal.resolve(null);
            setShapeModal(null);
          }}
          onSave={(meta) => {
            shapeModal.resolve(meta);
            setShapeModal(null);
          }}
        />
      )}

      {isOpenContainerPlatformsModal.isOpen && (
        <>
          <EditContainerPlatformsModal
            id={isOpenContainerPlatformsModal.id}
            onClose={() => setOpenContainerPlatforms({ isOpen: false, id: "" })}
            refetchData={refetchData}
          />
        </>
      )}

      {isOpenGeofenceModal.isOpen && (
        <EditGeofenceModal
          ref={geofenceModalRef}
          id={isOpenGeofenceModal.id}
          refetchData={refetchData}
          getPointsFromMap={() =>
            getGeofencePointsFromMap(isOpenGeofenceModal.id)
          }
          isContourEdited={true}
          isCollapsed={isGeofenceModalCollapsed}
          onStartEditContour={() => {
            startEditGeofenceContour(isOpenGeofenceModal.id);
            setGeofenceModalCollapsed(true);
          }}
          onClose={() => {
            setGeofenceModalCollapsed(false);
            setOpenGeofenceModal({ isOpen: false, id: "" });
          }}
        />
      )}
    </div>
  );
}
